import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useInvoice } from '@/hooks/useInvoice';
import { useQuotation } from '@/hooks/useQuotation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn, formatCurrencyWithSymbol, compressLogo } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, Plus, X, Save, Eye, HelpCircle, Crown, Upload, ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import type { LineItem, Tax, Discount } from '@/types/invoice';
import { ALL_CURRENCIES } from '@/types/invoice';
import { useToast } from '@/components/ui/use-toast';
import { TemplatePicker } from '@/components/invoice/TemplatePicker';
import { type TemplateId } from '@/components/invoice/InvoiceTemplates';
import { QuotationPrintView } from '@/components/invoice/QuotationPrintView';
import { Badge } from '@/components/ui/badge';
import { isValidEmail, isValidPhone } from '@/lib/validation';

// --- Payment Terms ---
const PAYMENT_TERMS = [
  { value: 'net7',       label: 'Net 7',       days: 7,    description: 'Due in 7 days',                  bestFor: 'Freelancers, small projects, repeat clients' },
  { value: 'net10',      label: 'Net 10',      days: 10,   description: 'Due in 10 days',                 bestFor: 'Service businesses, professional services' },
  { value: 'net15',      label: 'Net 15',      days: 15,   description: 'Due in 15 days',                 bestFor: 'Healthcare, IT services, consulting' },
  { value: 'net30',      label: 'Net 30',      days: 30,   description: 'Due in 30 days',                 bestFor: 'Standard B2B default across most industries' },
  { value: 'net45',      label: 'Net 45',      days: 45,   description: 'Due in 45 days',                 bestFor: 'Mid-size corporate clients' },
  { value: 'net60',      label: 'Net 60',      days: 60,   description: 'Due in 60 days',                 bestFor: 'Large corporations, manufacturing' },
  { value: 'net90',      label: 'Net 90',      days: 90,   description: 'Due in 90 days',                 bestFor: 'Enterprise, government contracts' },
  { value: 'netMonthly', label: 'Net Monthly', days: null, description: 'Due by end of the current month', bestFor: 'Recurring service contracts' },
  { value: 'custom',     label: 'Custom',      days: null, description: 'Pick a specific date',            bestFor: '' },
] as const;

type PaymentTermValue = typeof PAYMENT_TERMS[number]['value'];

/** Returns the valid-until date calculated from issue date + selected payment term */
const applyPaymentTerm = (term: PaymentTermValue, base: Date): Date => {
  if (term === 'netMonthly') {
    const d = new Date(base);
    d.setMonth(d.getMonth() + 1, 0); // last day of current month
    return d;
  }
  const found = PAYMENT_TERMS.find(t => t.value === term);
  if (!found || found.days === null) return base; // custom → no-op
  return new Date(base.getTime() + found.days * 24 * 60 * 60 * 1000);
};
// ----------------------

/** Returns the Terms & Conditions text for a given payment term */
const getTermsText = (term: PaymentTermValue): string => {
  switch (term) {
    case 'net7':       return 'Payment is due within 7 days of the invoice date.';
    case 'net10':      return 'Payment is due within 10 days of the invoice date.';
    case 'net15':      return 'Payment is due within 15 days of the invoice date.';
    case 'net30':      return 'Payment is due within 30 days of the invoice date.';
    case 'net45':      return 'Payment is due within 45 days of the invoice date.';
    case 'net60':      return 'Payment is due within 60 days of the invoice date.';
    case 'net90':      return 'Payment is due within 90 days of the invoice date.';
    case 'netMonthly': return 'Payment is due by the end of the current month.';
    default:           return '';
  }
};

export default function QuotationBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { isPro } = useSubscription();
  
  // Use useInvoice for profile listings (Businesses, Customers, Inventory Items)
  const { businesses, customers, inventoryItems = [] } = useInvoice();
  
  // Use useQuotation for quotation document CRUD operations
  const { getQuotation, createQuotation, updateQuotation, getNextQuotationNumber } = useQuotation();
  
  const { toast } = useToast();

  const [currentTab, setCurrentTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<TemplateId>('none');
  const [colorTheme, setColorTheme] = useState<string>('slate');
  const [errors, setErrors] = useState<{
    businessEmail?: boolean;
    businessPhone?: boolean;
    clientEmail?: boolean;
    clientPhone?: boolean;
  }>({});

  // Prefix selection (QT vs EST)
  const [prefix, setPrefix] = useState<'QT' | 'EST'>('QT');

  // Inventory autocomplete states
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const getFilteredInventory = (queryText: string) => {
    if (!inventoryItems) return [];
    const lowerQuery = (queryText || '').toLowerCase().trim();
    if (!lowerQuery) return inventoryItems;
    return inventoryItems.filter(item => 
      (item.name || '').toLowerCase().includes(lowerQuery) || 
      (item.code || '').toLowerCase().includes(lowerQuery)
    );
  };

  // Quotation data
  const [quotationNumber, setQuotationNumber] = useState('');
  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [validUntil, setValidUntil] = useState<Date>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  const [paymentTerm, setPaymentTerm] = useState<PaymentTermValue>('net30');
  const [status, setStatus] = useState<'draft' | 'sent' | 'accepted' | 'declined' | 'expired'>('draft');

  // Business info
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessLogoUrl, setBusinessLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload an image file.',
        variant: 'destructive',
      });
      return;
    }

    setLogoUploading(true);
    try {
      const base64Url = await compressLogo(file);
      setBusinessLogoUrl(base64Url);
      toast({
        title: 'Logo Uploaded',
        description: 'Business logo has been processed successfully.',
      });
    } catch (error) {
      console.error('Error processing logo:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to process logo image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setBusinessLogoUrl('');
    toast({
      title: 'Logo Removed',
      description: 'Business logo has been removed.',
    });
  };

  const handleBusinessSelect = (businessId: string) => {
    setSelectedBusinessId(businessId);
    const business = businesses.find(b => b.businessId === businessId);
    if (business) {
      setBusinessName(business.name);
      setBusinessEmail(business.email);
      setBusinessPhone(business.phone);
      setBusinessAddress(business.address);
      setBusinessLogoUrl(business.logoUrl || '');
    }
  };

  // Auto-select default business for Pro users on new quotation
  useEffect(() => {
    if (!id && isPro && businesses.length > 0 && !selectedBusinessId) {
      const defaultBusiness = businesses.find(b => b.isDefault) || businesses[0];
      if (defaultBusiness) {
        handleBusinessSelect(defaultBusiness.businessId);
      }
    }
  }, [businesses, isPro, id, selectedBusinessId]);

  // Client info
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');

  // Handler for customer profile selection
  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomerId(customerId);
    const customer = customers.find(c => c.customerId === customerId);
    if (customer) {
      setClientName(customer.name);
      setClientEmail(customer.email);
      setClientPhone(customer.phone);
      setClientAddress(customer.address);
    }
  };

  // Auto-select default customer for Pro users on new quotation
  useEffect(() => {
    if (!id && isPro && customers.length > 0 && !selectedCustomerId) {
      const defaultCustomer = customers.find(c => c.isDefault) || customers[0];
      if (defaultCustomer) {
        handleCustomerSelect(defaultCustomer.customerId);
      }
    }
  }, [customers, isPro, id, selectedCustomerId]);

  // Line items
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { 
      id: 'item-1',
      description: '', 
      quantity: 1, 
      unitPrice: 0,
      unit: 'pcs',
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false },
      subtotal: 0
    }
  ]);

  // Tax & Discount
  const [taxes, setTaxes] = useState<Tax[]>([{ type: 'none', value: 0, label: 'Tax', enabled: false }]);
  const [discount, setDiscount] = useState<Discount>({ type: 'none', value: 0, label: '', enabled: false });

  // Notes & Terms
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState(getTermsText('net30'));

  // Currency
  const [currency, setCurrency] = useState('USD');

  // Unsaved changes warning state & effects
  const initialStateRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);

  const handleInteraction = () => {
    if (!initialStateRef.current) {
      const currentVal = {
        quotationNumber,
        issueDate,
        validUntil,
        status,
        businessName,
        businessEmail,
        businessPhone,
        businessAddress,
        clientName,
        clientEmail,
        clientPhone,
        clientAddress,
        lineItems,
        taxes,
        discount,
        notes,
        terms,
        currency,
        selectedTemplateId,
        prefix
      };
      initialStateRef.current = JSON.stringify(currentVal);
    }
  };

  const checkIsDirty = () => {
    if (!initialStateRef.current) return false;
    const currentVal = {
      quotationNumber,
      issueDate,
      validUntil,
      status,
      businessName,
      businessEmail,
      businessPhone,
      businessAddress,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      lineItems,
      taxes,
      discount,
      notes,
      terms,
      currency,
      selectedTemplateId,
      prefix
    };
    return JSON.stringify(currentVal) !== initialStateRef.current;
  };

  const isDirty = checkIsDirty();
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave this page? You will lose unsaved data.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current &&
      !isSavingRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmLeave = window.confirm(
        'Are you sure you want to leave this page? You will lose unsaved data.'
      );
      if (confirmLeave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // Generate next quotation number when prefix changes or on new document
  useEffect(() => {
    const fetchNextNum = async () => {
      if (!id) {
        const nextNum = await getNextQuotationNumber(prefix);
        setQuotationNumber(nextNum);
      }
    };
    fetchNextNum();
  }, [prefix, id]);

  // Load quotation data if editing
  useEffect(() => {
    const initQuotation = async () => {
      try {
        if (id) {
          const quotation = await getQuotation(id);
          if (quotation) {
            setQuotationNumber(quotation.quotationNumber || '');
            setIssueDate(quotation.issueDate || new Date());
            setValidUntil(quotation.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
            setStatus(quotation.status || 'draft');
            setCurrency(quotation.currency || 'MYR');
            setNotes(quotation.notes || '');
            setTerms(quotation.terms || '');
            // When editing an existing quotation, preserve the saved valid-until date
            // and terms exactly — don't recalculate from any payment term.
            setPaymentTerm('custom');
            
            // Resolve prefix from loaded number
            if (quotation.quotationNumber?.startsWith('EST')) {
              setPrefix('EST');
            } else {
              setPrefix('QT');
            }

            // Restore template
            if (quotation.templateId) {
              setSelectedTemplateId(quotation.templateId as TemplateId);
            }
            if ((quotation as any).colorTheme) {
              setColorTheme((quotation as any).colorTheme);
            }

            // Restore Business profile info
            const busInfo = (quotation as any).from || (quotation as any)._businessInfo || {};
            setBusinessName(busInfo.name || '');
            setBusinessEmail(busInfo.email || '');
            setBusinessPhone(busInfo.phone || '');
            setBusinessAddress(busInfo.address || '');
            setBusinessLogoUrl(busInfo.logoUrl || '');

            // Restore client info
            const clientInfo = quotation.billTo || (quotation as any).to || {};
            setClientName(clientInfo.name || '');
            setClientEmail(clientInfo.email || '');
            setClientPhone(clientInfo.phone || '');
            setClientAddress(clientInfo.address || '');

            // Restore line items
            const savedItems = quotation.lineItems || (quotation as any).items || [];
            if (savedItems.length > 0) {
              setLineItems(savedItems.map((item: any, idx: number) => ({
                id: item.id || `item-${idx + 1}`,
                description: item.description || '',
                quantity: item.quantity || 1,
                unitPrice: item.unitPrice || item.rate || 0,
                unit: item.unit || 'pcs',
                lineTax: item.lineTax || { type: 'none', value: 0, label: 'None', enabled: false },
                lineDiscount: item.lineDiscount || { type: 'none', value: 0, enabled: false },
                subtotal: item.subtotal || item.amount || 0
              })));
            }

            // Restore global taxes
            const settings = quotation.settings || {};
            const headerTaxes = settings.headerTax?.taxes;
            if (headerTaxes && headerTaxes.length > 0) {
              setTaxes(headerTaxes.map((t: any) => ({
                type: t.type || 'none',
                value: t.value || 0,
                label: t.label || 'Tax',
                enabled: t.enabled ?? (t.type !== 'none'),
              })));
            } else {
              const taxData = (quotation as any).tax;
              if (taxData && taxData.type && taxData.type !== 'none') {
                setTaxes([{
                  type: taxData.type,
                  value: taxData.value || 0,
                  label: taxData.label || 'Tax',
                  enabled: true,
                }]);
              } else {
                setTaxes([{ type: 'none', value: 0, label: 'Tax', enabled: false }]);
              }
            }
            if (settings.headerDiscount?.enabled) {
              setDiscount({
                type: settings.headerDiscount.type || 'none',
                value: settings.headerDiscount.value || 0,
                enabled: true,
                label: settings.headerDiscount.label || ''
              });
            }
          }
        }
      } catch (error) {
        console.error('Error initializing quotation:', error);
      }
    };
    initQuotation();
  }, [id]);

  // Auto-set currency to default currency from user profile on new quotation
  useEffect(() => {
    if (!id && userProfile?.defaultCurrency) {
      setCurrency(userProfile.defaultCurrency);
    }
  }, [userProfile, id]);

  // Calculations
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item), 0);
  };

  const calculateTaxAmount = () => {
    const subtotal = calculateSubtotal();
    return taxes.reduce((acc, curr) => {
      if (!curr.enabled || curr.type === 'none') return acc;
      const val = Number(curr.value) || 0;
      if (curr.type === 'percentage' || curr.type === 'vat' || curr.type === 'sst') {
        acc += (subtotal * val) / 100;
      } else if (curr.type === 'fixed') {
        acc += val;
      }
      return acc;
    }, 0);
  };

  const calculateSingleTaxAmount = (t: Tax) => {
    if (!t.enabled || t.type === 'none') return 0;
    const subtotal = calculateSubtotal();
    const val = Number(t.value) || 0;
    if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
      return (subtotal * val) / 100;
    } else if (t.type === 'fixed') {
      return val;
    }
    return 0;
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    const val = Number(discount.value) || 0;
    if (discount.type === 'percentage') {
      return (subtotal * val) / 100;
    } else if (discount.type === 'fixed') {
      return val;
    }
    return 0;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const taxAmount = calculateTaxAmount();
    const discountAmount = calculateDiscountAmount();
    return subtotal + taxAmount - discountAmount;
  };

  // Line item handlers
  const addLineItem = () => {
    setLineItems([...lineItems, { 
      id: `item-${lineItems.length + 1}`,
      description: '', 
      quantity: 1, 
      unitPrice: 0,
      unit: 'pcs',
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false },
      subtotal: 0
    }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: string, value: any) => {
    const updated = [...lineItems];
    const item = { ...updated[index], [field]: value };
    
    // Recalculate subtotal
    if (field === 'quantity' || field === 'unitPrice') {
      item.subtotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
    }
    updated[index] = item;
    setLineItems(updated);
  };

  const updateLineItemTax = (index: number, taxType: string, value: number | string) => {
    const updated = [...lineItems];
    const taxLabels: Record<string, string> = {
      'none': 'None',
      'vat': 'VAT',
      'sst': 'SST',
      'percentage': 'Tax',
      'fixed': 'Tax'
    };
    updated[index].lineTax = { 
      type: taxType as any, 
      value: value, 
      label: taxLabels[taxType] || 'Tax',
      enabled: taxType !== 'none'
    };
    setLineItems(updated);
  };

  const updateLineItemDiscount = (index: number, discountType: string, value: number | string) => {
    const updated = [...lineItems];
    updated[index].lineDiscount = { 
      type: discountType as any, 
      value: value,
      enabled: discountType !== 'none'
    };
    setLineItems(updated);
  };

  const calculateLineItemTotal = (item: LineItem) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unitPrice) || 0;
    let total = qty * price;
    
    if (item.lineTax && item.lineTax.type !== 'none') {
      const taxVal = Number(item.lineTax.value) || 0;
      if (item.lineTax.type === 'percentage' || item.lineTax.type === 'vat' || item.lineTax.type === 'sst') {
        total += (total * taxVal) / 100;
      } else if (item.lineTax.type === 'fixed') {
        total += taxVal;
      }
    }
    
    if (item.lineDiscount && item.lineDiscount.type !== 'none') {
      const discVal = Number(item.lineDiscount.value) || 0;
      if (item.lineDiscount.type === 'percentage') {
        total -= (total * discVal) / 100;
      } else if (item.lineDiscount.type === 'fixed') {
        total -= discVal;
      }
    }
    
    return total;
  };

  // Save Quotation
  const handleSave = async () => {
    if (!user) return;
    
    if (!quotationNumber) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a Quotation Number.',
        variant: 'destructive',
      });
      return;
    }

    // Validate email and phone
    const newErrors: typeof errors = {};
    let firstErrorTab: string | null = null;

    if (businessEmail && !isValidEmail(businessEmail)) {
      newErrors.businessEmail = true;
      if (!firstErrorTab) firstErrorTab = 'basic';
    }

    if (businessPhone && !isValidPhone(businessPhone)) {
      newErrors.businessPhone = true;
      if (!firstErrorTab) firstErrorTab = 'basic';
    }

    if (clientEmail && !isValidEmail(clientEmail)) {
      newErrors.clientEmail = true;
      if (!firstErrorTab) firstErrorTab = 'client';
    }

    if (clientPhone && !isValidPhone(clientPhone)) {
      newErrors.clientPhone = true;
      if (!firstErrorTab) firstErrorTab = 'client';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (firstErrorTab) {
        setCurrentTab(firstErrorTab);
      }
      toast({
        title: 'Validation Error',
        description: 'Please correct the highlighted fields in the form.',
        variant: 'destructive',
      });
      return;
    }

    setErrors({});
    isSavingRef.current = true;
    setSaving(true);
    try {
      const quotationData: any = {
        businessId: 'default',
        quotationNumber,
        issueDate,
        validUntil,
        status,
        currency,
        templateId: selectedTemplateId,
        colorTheme: colorTheme,
        billTo: {
          name: clientName,
          companyName: clientName,
          address: clientAddress,
          email: clientEmail,
          phone: clientPhone,
          taxNumber: '',
        },
        lineItems: lineItems
          .filter(item => item.description)
          .map((item, index) => ({
            id: item.id || `item-${index + 1}`,
            description: item.description,
            quantity: Number(item.quantity) || 0,
            unitPrice: Number(item.unitPrice) || 0,
            unit: item.unit || 'pcs',
            lineTax: {
              enabled: item.lineTax?.type !== 'none',
              type: item.lineTax?.type || 'none',
              label: item.lineTax?.label || '',
              value: Number(item.lineTax?.value) || 0,
            },
            lineDiscount: {
              enabled: item.lineDiscount?.type !== 'none',
              type: item.lineDiscount?.type || 'none',
              value: Number(item.lineDiscount?.value) || 0,
            },
            subtotal: (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
          })),
        settings: {
          headerTax: {
            enabled: taxes.some(t => t.type !== 'none'),
            taxes: taxes.map(t => ({
              label: t.label || 'Tax',
              type: t.type,
              value: Number(t.value) || 0,
              enabled: t.type !== 'none',
            })),
          },
          headerDiscount: {
            enabled: discount.type !== 'none',
            type: discount.type as 'percentage' | 'fixed',
            value: Number(discount.value) || 0,
            label: discount.label || '',
          },
          customTaxes: [],
          showLineTax: false,
          showLineDiscount: false,
          applyTaxOn: 'subtotal' as const,
        },
        notes,
        terms,
        subtotal: calculateSubtotal(),
        totalTax: calculateTaxAmount(),
        totalDiscount: calculateDiscountAmount(),
        totalAmount: calculateTotal(),
        logoUrl: businessLogoUrl,
        // Keep old structure keys for backwards compatibility in templates rendering
        from: {
          name: businessName,
          email: businessEmail,
          phone: businessPhone,
          address: businessAddress,
          logoUrl: businessLogoUrl,
        },
        to: {
          name: clientName,
          email: clientEmail,
          phone: clientPhone,
          address: clientAddress,
        },
        items: lineItems
          .filter(item => item.description)
          .map(item => ({
            description: item.description,
            quantity: Number(item.quantity) || 0,
            rate: Number(item.unitPrice) || 0,
            amount: (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
          })),
        tax: taxes.length > 0 && taxes[0].type !== 'none'
          ? Object.fromEntries(
              Object.entries({
                ...taxes[0],
                value: Number(taxes[0].value) || 0
              }).filter(([_, v]) => v !== undefined)
            )
          : { type: 'none', value: 0, label: 'Tax', enabled: false },
        discount: Object.fromEntries(
          Object.entries({
            ...discount,
            value: Number(discount.value) || 0
          }).filter(([_, v]) => v !== undefined)
        ),
        taxAmount: calculateTaxAmount(),
        discountAmount: calculateDiscountAmount(),
        total: calculateTotal(),
      };
      
      if (id) {
        await updateQuotation(id, quotationData);
        initialStateRef.current = null;
        navigate(`/quotations/preview/${id}`);
      } else {
        const newId = await createQuotation(quotationData);
        initialStateRef.current = null;
        navigate(`/quotations/preview/${newId}`);
      }
    } catch (error) {
      console.error('Error saving quotation:', error);
      isSavingRef.current = false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" onFocusCapture={handleInteraction} onPointerDownCapture={handleInteraction}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold">
              {id ? 'Edit Quotation / Estimate' : 'Create New Quotation / Estimate'}
            </h1>
            <p className="text-muted-foreground mt-1">
              Document #{quotationNumber}
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showGuide} onOpenChange={setShowGuide}>
              <HelpCircle 
                className="h-6 w-6 text-muted-foreground hover:text-foreground cursor-pointer self-center" 
                onClick={() => setShowGuide(true)} 
              />
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Quotation & Estimate Guide</DialogTitle>
                  <DialogDescription>
                    Create quotations or estimates in a few quick steps. Switch prefix between **QT** and **EST** to configure your reference number format.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm pt-2">
                  <p>
                    <strong>Prefix Toggle:</strong> In the Basic tab, select either "Quotation" or "Estimate" to load the next document number sequence dynamically.
                  </p>
                  <p>
                    <strong>Valid Until Date:</strong> Set when the quotation expires. Defaults to 30 days from creation.
                  </p>
                  <p>
                    <strong>Pro Auto-fill:</strong> If you are a Pro subscriber, default Business and Client profiles can be loaded instantly with automatic search from your inventory items & services database.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Document'}
            </Button>
          </div>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Panel - Form */}
          <div className="lg:col-span-5 space-y-6">
            <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic" className="flex items-center gap-1">
                  1. Basic
                  {(errors.businessEmail || errors.businessPhone) && (
                    <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="client" className="flex items-center gap-1">
                  2. Client
                  {(errors.clientEmail || errors.clientPhone) && (
                    <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="items">3. Items</TabsTrigger>
                <TabsTrigger value="settings">4. Template</TabsTrigger>
              </TabsList>

              {/* Tab 1: Basic details */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Basic Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Prefix Type and Document Number */}
                    {!id && (
                      <div className="space-y-2">
                        <Label>Document Type (Prefix)</Label>
                        <Select 
                          value={prefix} 
                          onValueChange={(val: 'QT' | 'EST') => setPrefix(val)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Prefix" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="QT">Quotation (QT-)</SelectItem>
                            <SelectItem value="EST">Estimate (EST-)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Document Reference Number</Label>
                        <Input
                          value={quotationNumber}
                          onChange={(e) => setQuotationNumber(e.target.value)}
                          placeholder="e.g. QT-0001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select value={currency} onValueChange={setCurrency}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                             {ALL_CURRENCIES.map((curr) => (
                               <SelectItem key={curr.code} value={curr.code}>
                                 {curr.code} ({curr.symbol}) - {curr.name}
                               </SelectItem>
                             ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 flex flex-col">
                        <Label>Issue Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn("w-full justify-start text-left font-normal", !issueDate && "text-muted-foreground")}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {issueDate ? format(issueDate, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={issueDate}
                              onSelect={(date) => date && setIssueDate(date)}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2 flex flex-col">
                        <Label>Valid Until</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn("w-full justify-start text-left font-normal", !validUntil && "text-muted-foreground")}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {validUntil ? format(validUntil, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={validUntil}
                              onSelect={(date) => { if (date) { setValidUntil(date); setPaymentTerm('custom'); } }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    {/* Payment Terms */}
                    <div className="space-y-2">
                      <Label>Payment Terms</Label>
                      <Select
                        value={paymentTerm}
                        onValueChange={(val: PaymentTermValue) => {
                          setPaymentTerm(val);
                          if (val !== 'custom') {
                            setValidUntil(applyPaymentTerm(val, issueDate));
                            setTerms(getTermsText(val));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment terms" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_TERMS.map((term) => (
                            <SelectItem key={term.value} value={term.value}>
                              <div className="flex flex-col">
                                <span className="font-medium">{term.label}</span>
                                {term.bestFor && (
                                  <span className="text-xs text-muted-foreground">{term.description} · {term.bestFor}</span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={status} 
                        onValueChange={(val: any) => setStatus(val)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Business Profile Auto-fill (Pro Users) */}
                    <div className="pt-4 border-t space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className="text-base font-semibold">Your Business Details</Label>
                        {isPro ? (
                          businesses.length > 0 && (
                            <Select value={selectedBusinessId} onValueChange={handleBusinessSelect}>
                              <SelectTrigger className="w-64">
                                <SelectValue placeholder="Load Business Profile" />
                              </SelectTrigger>
                              <SelectContent>
                                {businesses.map((b) => (
                                  <SelectItem key={b.businessId} value={b.businessId}>
                                    {b.name} {b.isDefault && '(Default)'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 font-semibold text-[10px]">
                            PRO Auto-fill
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-3">
                        {/* Business Logo Upload Section */}
                        <div className="space-y-2 border-b pb-4">
                          <Label>Business Logo</Label>
                          {!isPro ? (
                            <div className="border border-dashed rounded-lg p-4 bg-slate-50 flex items-center justify-between text-left">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                                  <Crown className="w-5 h-5" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-800 text-sm">Business Logo (Pro Feature)</h4>
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    Upgrade to Pro to upload logo and brand quotations.
                                  </p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                className="bg-amber-500 hover:bg-amber-600 text-white font-bold shrink-0"
                                onClick={() => navigate('/subscription')}
                              >
                                Upgrade
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col sm:flex-row gap-4 items-center">
                              {businessLogoUrl ? (
                                <div className="relative group w-32 h-20 bg-slate-100 border rounded-lg overflow-hidden flex items-center justify-center">
                                  <img
                                    src={businessLogoUrl}
                                    alt="Logo preview"
                                    className="max-h-full max-w-full object-contain"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold"
                                  >
                                    Remove Logo
                                  </button>
                                </div>
                              ) : (
                                <div className="w-32 h-20 bg-slate-50 border border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                                  <ImageIcon className="w-8 h-8 opacity-45" />
                                </div>
                              )}

                              <div className="flex-1 space-y-2 text-left">
                                <div className="flex items-center gap-2">
                                  <Input
                                    id="logo-upload-manual-quote"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    disabled={logoUploading}
                                    className="hidden"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    disabled={logoUploading}
                                    onClick={() => document.getElementById('logo-upload-manual-quote')?.click()}
                                  >
                                    {logoUploading ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {businessLogoUrl ? 'Change Logo' : 'Upload Logo'}
                                      </>
                                    )}
                                  </Button>
                                  {businessLogoUrl && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      onClick={handleRemoveLogo}
                                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>
                                <p className="text-[11px] text-muted-foreground">
                                  Supported formats: PNG, JPG, SVG. Max size: 2MB. Recommended: Landscape ratio.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Business Name</Label>
                          <Input
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            placeholder="Your Business Name"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={businessEmail}
                              onChange={(e) => {
                                setBusinessEmail(e.target.value);
                                if (errors.businessEmail) {
                                  setErrors(prev => ({ ...prev, businessEmail: false }));
                                }
                              }}
                              placeholder="info@yourcompany.com"
                              className={cn(errors.businessEmail && "border-red-500 focus-visible:ring-red-500")}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                              value={businessPhone}
                              onChange={(e) => {
                                setBusinessPhone(e.target.value);
                                if (errors.businessPhone) {
                                  setErrors(prev => ({ ...prev, businessPhone: false }));
                                }
                              }}
                              placeholder="+60 3-1234 5678"
                              className={cn(errors.businessPhone && "border-red-500 focus-visible:ring-red-500")}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Textarea
                            value={businessAddress}
                            onChange={(e) => setBusinessAddress(e.target.value)}
                            placeholder="Your business address"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-4 border-t mt-6">
                      <Button type="button" onClick={() => setCurrentTab('client')}>
                        Next: Client Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tab 2: Client details */}
              <TabsContent value="client" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Client Details</CardTitle>
                      {isPro ? (
                        customers.length > 0 && (
                          <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                            <SelectTrigger className="w-64">
                              <SelectValue placeholder="Load Customer Profile" />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((c) => (
                                <SelectItem key={c.customerId} value={c.customerId}>
                                  {c.name} {c.companyName && `(${c.companyName})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )
                      ) : (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 font-semibold text-[10px]">
                          PRO Auto-fill
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Client Name</Label>
                      <Input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Client / Customer Name"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Client Email</Label>
                        <Input
                          type="email"
                          value={clientEmail}
                          onChange={(e) => {
                            setClientEmail(e.target.value);
                            if (errors.clientEmail) {
                              setErrors(prev => ({ ...prev, clientEmail: false }));
                            }
                          }}
                          placeholder="client@example.com"
                          className={cn(errors.clientEmail && "border-red-500 focus-visible:ring-red-500")}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Client Phone</Label>
                        <Input
                          value={clientPhone}
                          onChange={(e) => {
                            setClientPhone(e.target.value);
                            if (errors.clientPhone) {
                              setErrors(prev => ({ ...prev, clientPhone: false }));
                            }
                          }}
                          placeholder="+60 12-345 6789"
                          className={cn(errors.clientPhone && "border-red-500 focus-visible:ring-red-500")}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Client Address</Label>
                      <Textarea
                        value={clientAddress}
                        onChange={(e) => setClientAddress(e.target.value)}
                        placeholder="Client billing address"
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex justify-end pt-4 border-t mt-6">
                      <Button type="button" onClick={() => setCurrentTab('items')}>
                        Next: Line Items
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tab 3: Line Items */}
              <TabsContent value="items" className="space-y-4 mt-4">
                {lineItems.map((item, index) => (
                  <Card key={item.id || index}>
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <Label className="text-base font-bold">Item {index + 1}</Label>
                          {lineItems.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        {/* Description input with Autocomplete trigger for Pro users */}
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-500">Description</span>
                            {!isPro && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] py-0 px-1 font-semibold">
                                PRO Auto-fill
                              </Badge>
                            )}
                          </div>
                          <div className="relative">
                            <Input
                              placeholder="Item description"
                              value={item.description}
                              onChange={(e) => {
                                updateLineItem(index, 'description', e.target.value);
                                setSearchQuery(e.target.value);
                                setFocusedItemIndex(index);
                              }}
                              onFocus={() => {
                                setFocusedItemIndex(index);
                                setSearchQuery(item.description);
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  setFocusedItemIndex(null);
                                }, 250);
                              }}
                            />
                            {focusedItemIndex === index && (
                              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {isPro ? (
                                  getFilteredInventory(searchQuery).length > 0 ? (
                                    getFilteredInventory(searchQuery).map((invItem) => (
                                      <button
                                        key={invItem.itemId}
                                        type="button"
                                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex justify-between items-center border-b last:border-b-0"
                                        onMouseDown={() => {
                                          const updated = [...lineItems];
                                          updated[index] = {
                                            ...updated[index],
                                            description: invItem.name,
                                            unitPrice: invItem.rate,
                                            unit: invItem.unit || 'pcs',
                                            subtotal: Number(invItem.rate) * (Number(item.quantity) || 1)
                                          };
                                          setLineItems(updated);
                                        }}
                                      >
                                        <div>
                                          <div className="font-semibold text-gray-800">{invItem.name}</div>
                                          <div className="text-xs text-muted-foreground font-mono">Code: {invItem.code}</div>
                                        </div>
                                        <div className="text-right text-xs">
                                          <span className="font-medium text-indigo-600">${Number(invItem.rate).toFixed(2)}</span>
                                          <span className="text-muted-foreground"> / {invItem.unit}</span>
                                        </div>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-3 text-xs text-muted-foreground text-center space-y-1">
                                      <div>No matching inventory items or services found (Type name or code).</div>
                                      <div className="text-[10px] text-indigo-500 font-medium">You can still type custom text here to enter a manual item description.</div>
                                    </div>
                                  )
                                ) : (
                                  <div className="p-3 text-xs text-amber-900 bg-amber-50/90 rounded-md">
                                    <div className="font-semibold flex items-center gap-1 mb-1 text-amber-950">
                                      <Crown className="h-3.5 w-3.5 text-amber-600" />
                                      Inventory Items & Services (PRO Feature)
                                    </div>
                                    <p className="text-amber-800 text-[11px] mb-2 leading-snug">
                                      Save products, services, codes, and prices in your database and search them here to auto-fill.
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="h-7 text-[10px] w-full bg-amber-500 hover:bg-amber-600 text-white font-bold"
                                      onMouseDown={() => navigate('/subscription')}
                                    >
                                      Upgrade to Pro
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Quantity, Rate, Unit */}
                        <div className="grid grid-cols-4 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity ?? ''}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Unit</Label>
                            <Input
                              value={item.unit}
                              onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                              placeholder="pcs"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Rate</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unitPrice ?? ''}
                              onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Amount</Label>
                            <Input
                              type="number"
                              value={((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)).toFixed(2)}
                              readOnly
                              disabled
                            />
                          </div>
                        </div>

                        {/* Item Tax */}
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Tax (per item)</Label>
                            <div className="flex gap-2">
                              <Select 
                                value={item.lineTax?.type || 'none'} 
                                onValueChange={(value) => updateLineItemTax(index, value, item.lineTax?.value || 0)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="vat">VAT %</SelectItem>
                                  <SelectItem value="sst">SST %</SelectItem>
                                  <SelectItem value="percentage">Tax %</SelectItem>
                                  <SelectItem value="fixed">Fixed</SelectItem>
                                </SelectContent>
                              </Select>
                              {item.lineTax?.type !== 'none' && (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-24"
                                  value={item.lineTax?.value ?? ''}
                                  onChange={(e) => updateLineItemTax(index, item.lineTax?.type || 'none', e.target.value as any)}
                                  onFocus={(e) => e.target.select()}
                                />
                              )}
                            </div>
                          </div>

                          {/* Item Discount */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Discount (per item)</Label>
                            <div className="flex gap-2">
                              <Select 
                                value={item.lineDiscount?.type || 'none'} 
                                onValueChange={(value) => updateLineItemDiscount(index, value, item.lineDiscount?.value || 0)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="percentage">%</SelectItem>
                                  <SelectItem value="fixed">Fixed</SelectItem>
                                </SelectContent>
                              </Select>
                              {item.lineDiscount?.type !== 'none' && (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-24"
                                  value={item.lineDiscount?.value ?? ''}
                                  onChange={(e) => updateLineItemDiscount(index, item.lineDiscount?.type || 'none', e.target.value as any)}
                                  onFocus={(e) => e.target.select()}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button variant="outline" onClick={addLineItem} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Item
                </Button>

                {/* Subtotals & Taxes */}
                <div className="pt-4 border-t space-y-4">
                  <div className="flex justify-between font-semibold">
                    <span>Subtotal:</span>
                    <span>{formatCurrencyWithSymbol(currency, calculateSubtotal())}</span>
                  </div>

                  {/* Global Tax */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium">Global Taxes</Label>
                      {isPro && userProfile?.predefinedTaxes && userProfile.predefinedTaxes.length > 0 && (
                        <Select
                          onValueChange={(taxIndexStr) => {
                            const taxIndex = parseInt(taxIndexStr, 10);
                            const preTax = userProfile.predefinedTaxes?.[taxIndex];
                            if (preTax) {
                              const updated = [...taxes];
                              if (updated.length === 1 && updated[0].type === 'none') {
                                updated[0] = {
                                  type: preTax.type,
                                  value: preTax.value,
                                  label: preTax.label,
                                  enabled: true
                                };
                              } else {
                                updated.push({
                                  type: preTax.type,
                                  value: preTax.value,
                                  label: preTax.label,
                                  enabled: true
                                });
                              }
                              setTaxes(updated);
                            }
                          }}
                        >
                          <SelectTrigger className="w-44 h-8 text-xs">
                            <SelectValue placeholder="Apply Saved Tax" />
                          </SelectTrigger>
                          <SelectContent>
                            {userProfile.predefinedTaxes.map((tax: Tax, idx: number) => (
                              <SelectItem key={idx} value={idx.toString()} className="text-xs">
                                {tax.label} ({tax.type === 'fixed' ? 'Fixed' : tax.value + '%'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isPro 
                        ? "Select a predefined tax rate to auto-fill or enter a custom rate manually."
                        : "Enter a tax rate manually. Upgrade to Pro to save predefined tax rates (e.g. VAT, GST, SST) for one-click selection."}
                    </p>
                    <div className="space-y-2">
                      {taxes.map((t, index) => (
                        <div key={index} className="space-y-1">
                          <div className="flex gap-2 items-center">
                            <Select
                              value={t.type}
                              onValueChange={(val: any) => {
                                const labels: Record<string, string> = {
                                  none: 'Tax', vat: 'VAT', sst: 'SST', percentage: 'Tax', fixed: 'Tax'
                                };
                                const updated = [...taxes];
                                updated[index] = {
                                  ...updated[index],
                                  type: val,
                                  label: labels[val] || 'Tax',
                                  enabled: val !== 'none'
                                };
                                setTaxes(updated);
                              }}
                            >
                              <SelectTrigger className="w-32 shrink-0">
                                <SelectValue placeholder="Tax Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="vat">VAT %</SelectItem>
                                <SelectItem value="sst">SST %</SelectItem>
                                <SelectItem value="percentage">Tax %</SelectItem>
                                <SelectItem value="fixed">Fixed Amount</SelectItem>
                              </SelectContent>
                            </Select>

                            {t.type !== 'none' && (
                              <div className="relative flex-1">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={t.value ?? ''}
                                  onChange={(e) => {
                                    const updated = [...taxes];
                                    updated[index] = {
                                      ...updated[index],
                                      value: e.target.value
                                    };
                                    setTaxes(updated);
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  className="pr-12"
                                  placeholder="0.00"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium pointer-events-none">
                                  {t.type === 'fixed' ? currency : '%'}
                                </span>
                              </div>
                            )}

                            {index > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const updated = taxes.filter((_, i) => i !== index);
                                  setTaxes(updated);
                                }}
                                className="h-9 w-9 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          {t.type !== 'none' && (
                            <div className="flex justify-between text-xs text-muted-foreground px-1">
                              <span>{t.label || 'Tax'} Amount:</span>
                              <span>{formatCurrencyWithSymbol(currency, calculateSingleTaxAmount(t))}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {taxes.some(t => t.type !== 'none') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTaxes([...taxes, { type: 'none', value: 0, label: 'Tax', enabled: false }]);
                        }}
                        className="text-xs flex items-center gap-1 hover:bg-accent hover:text-accent-foreground transition-all duration-200"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add tax
                      </Button>
                    )}
                  </div>

                  {/* Global Discount */}
                  <div className="space-y-2">
                    <Label>Global Discount</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={discount.type} 
                        onValueChange={(val: any) => setDiscount({ ...discount, type: val, enabled: val !== 'none' })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="percentage">Percentage %</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                      {discount.type !== 'none' && (
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={discount.value ?? ''}
                          onChange={(e) => setDiscount({ ...discount, value: e.target.value })}
                          onFocus={(e) => e.target.select()}
                        />
                      )}
                    </div>
                  </div>

                  {/* Grand Total */}
                  <div className="flex justify-between border-t pt-4 text-xl font-bold text-primary">
                    <span>Grand Total:</span>
                    <span>{formatCurrencyWithSymbol(currency, calculateTotal())}</span>
                  </div>

                  {/* Notes & Terms */}
                  <div className="space-y-3 pt-4 border-t">
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add special notes, payment info, etc."
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Terms & Conditions</Label>
                      <Textarea
                        value={terms}
                        onChange={(e) => setTerms(e.target.value)}
                        placeholder="Quotation validity details"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4 border-t mt-6">
                  <Button type="button" onClick={() => setCurrentTab('settings')}>
                    Next: Pick Template Style
                  </Button>
                </div>
              </TabsContent>

              {/* Tab 4: Template styling */}
              <TabsContent value="settings" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Pick Document Style Template</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">1. Layout Format</Label>
                        <p className="text-xs text-muted-foreground font-medium">Choose a layout format style for your quotation</p>
                        <TemplatePicker
                          selectedId={selectedTemplateId}
                          onChange={setSelectedTemplateId}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">2. Color Accent</Label>
                        <p className="text-xs text-muted-foreground font-medium">Choose a color accent for headers and highlights</p>
                        <div className="flex flex-wrap gap-2.5 pt-1">
                          {[
                            { id: 'slate', name: 'Slate Gray', bg: 'bg-slate-500' },
                            { id: 'blue', name: 'Corporate Blue', bg: 'bg-blue-600' },
                            { id: 'black', name: 'Midnight Black', bg: 'bg-slate-950' },
                            { id: 'red', name: 'Crimson Red', bg: 'bg-red-600' },
                            { id: 'green', name: 'Emerald Green', bg: 'bg-emerald-600' },
                            { id: 'purple', name: 'Royal Purple', bg: 'bg-purple-600' },
                            { id: 'orange', name: 'Warm Amber', bg: 'bg-amber-500' },
                          ].map((theme) => {
                            const isSelected = colorTheme === theme.id;
                            return (
                              <button
                                key={theme.id}
                                type="button"
                                onClick={() => setColorTheme(theme.id)}
                                className={cn(
                                  "w-9 h-9 rounded-full flex items-center justify-center transition-all border-2",
                                  isSelected 
                                    ? "ring-2 ring-offset-2 ring-indigo-500 scale-105 border-white shadow-md" 
                                    : "border-transparent hover:scale-105 hover:shadow-sm"
                                )}
                                title={theme.name}
                              >
                                <span className={cn("w-7 h-7 rounded-full block shadow-inner", theme.bg)} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Preview */}
          <div className="lg:col-span-7 lg:sticky lg:top-8 h-fit min-w-0">
            <Card className="overflow-hidden w-full">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Eye className="h-5 w-5 mr-2" />
                  Quotation Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                <div className="overflow-x-auto w-full">
                  <div className="min-w-[600px] lg:min-w-0">
                    <QuotationPrintView quotation={{
                      businessId: 'default',
                      quotationNumber,
                      issueDate,
                      validUntil,
                      status,
                      currency,
                      templateId: selectedTemplateId,
                      colorTheme: colorTheme,
                      billTo: {
                        name: clientName,
                        companyName: clientName,
                        address: clientAddress,
                        email: clientEmail,
                        phone: clientPhone,
                        taxNumber: '',
                      },
                      lineItems: lineItems
                        .filter(item => item.description)
                        .map((item, index) => ({
                          id: `item-${index + 1}`,
                          description: item.description,
                          quantity: Number(item.quantity) || 0,
                          unitPrice: Number(item.rate) || 0,
                          unit: 'pcs',
                          lineTax: {
                            enabled: item.lineTax?.type !== 'none',
                            type: item.lineTax?.type || 'none',
                            label: item.lineTax?.label || '',
                            value: Number(item.lineTax?.value) || 0,
                          },
                          lineDiscount: {
                            enabled: item.lineDiscount?.type !== 'none',
                            type: item.lineDiscount?.type || 'none',
                            value: Number(item.lineDiscount?.value) || 0,
                          },
                          subtotal: Number(item.amount) || 0,
                        })),
                      settings: {
                        headerTax: {
                          enabled: taxes.some(t => t.type !== 'none'),
                          taxes: taxes.map(t => ({
                            label: t.label || 'Tax',
                            type: t.type,
                            value: Number(t.value) || 0,
                            enabled: t.type !== 'none',
                          })),
                        },
                        headerDiscount: {
                          enabled: discount.type !== 'none',
                          type: discount.type as 'percentage' | 'fixed',
                          value: Number(discount.value) || 0,
                          label: discount.label || '',
                        },
                        customTaxes: [],
                        showLineTax: false,
                        showLineDiscount: false,
                        applyTaxOn: 'subtotal' as const,
                      },
                      notes,
                      terms,
                      subtotal: calculateSubtotal(),
                      totalTax: calculateTaxAmount(),
                      totalDiscount: calculateDiscountAmount(),
                      totalAmount: calculateTotal(),
                      logoUrl: businessLogoUrl,
                      from: {
                        name: businessName,
                        email: businessEmail,
                        phone: businessPhone,
                        address: businessAddress,
                        logoUrl: businessLogoUrl,
                      },
                      to: {
                        name: clientName,
                        email: clientEmail,
                        phone: clientPhone,
                        address: clientAddress,
                      },
                      items: lineItems
                        .filter(item => item.description)
                        .map(item => ({
                          description: item.description,
                          quantity: Number(item.quantity) || 0,
                          rate: Number(item.rate) || 0,
                          amount: Number(item.amount) || 0,
                        })),
                      tax: taxes.length > 0 && taxes[0].type !== 'none'
                        ? Object.fromEntries(
                            Object.entries({
                              ...taxes[0],
                              value: Number(taxes[0].value) || 0
                            }).filter(([_, v]) => v !== undefined)
                          )
                        : { type: 'none', value: 0, label: 'Tax', enabled: false },
                      discount: Object.fromEntries(
                        Object.entries({
                          ...discount,
                          value: Number(discount.value) || 0
                        }).filter(([_, v]) => v !== undefined)
                      ),
                      taxAmount: calculateTaxAmount(),
                      discountAmount: calculateDiscountAmount(),
                      total: calculateTotal(),
                    } as any} noShadow />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
